"use client";

import Link from "next/link";
import type { Group } from "@theta/api-client";
import { memberCountLabel } from "@/lib/counts";
import { COVER_ASPECT_CLASS } from "@/lib/coverAspect";

/** Exhaustive by TYPE: a fourth privacy level stops this file compiling. */
const PRIVACY_LABELS: Record<Group["privacyLevel"], { label: string; className: string }> = {
  public_group: {
    label: "Offentlig",
    className: "bg-green-50 text-green-700",
  },
  private_group: {
    label: "Privat",
    className: "bg-amber-50 text-amber-700",
  },
  secret_group: {
    label: "Hemmelig",
    className: "bg-gray-100 text-muted-foreground dark:bg-gray-800",
  },
};

export function GroupCard({ group }: { group: Group }) {
  const isMember = group.viewerMembership?.status === "active";
  const privacy = PRIVACY_LABELS[group.privacyLevel];
  const isHidden = group.privacyLevel === "secret_group";
  const description = group.description
    ? group.description.replace(/&amp;/g, "&").replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/&quot;/g, '"').replace(/&#39;/g, "'")
    : null;

  return (
    <Link
      href={`/grupper/${group.slug}`}
      className={`bg-surface rounded-xl border border-border overflow-hidden hover:shadow-md transition-shadow block${isHidden ? " opacity-60" : ""}`}
    >
      {/*
        THE SAME RATIO THE COVER WAS CROPPED TO (OSO-632, and OSO-626 before it).

        Both branches, and they have to stay together: the placeholder is what a
        group with no cover gets, so a fixed height on one and a ratio on the
        other would make two cards in one grid different heights.

        This was `h-32` — a fixed 128px against a 3:1 crop, so the thumbnail
        re-cropped the cover at whatever ratio the column happened to give. Same
        bug as the create-group preview, one screen over.
      */}
      {group.coverImageUrl ? (
        <img
          src={group.coverImageUrl}
          alt=""
          className={`w-full ${COVER_ASPECT_CLASS} object-cover`}
        />
      ) : (
        <div className={`w-full ${COVER_ASPECT_CLASS} bg-gradient-to-br from-indigo-100 to-indigo-50 dark:from-indigo-900/50 dark:to-indigo-950 flex items-center justify-center`}>
          <span className="text-4xl font-bold text-indigo-300 dark:text-indigo-700">
            {/* By code POINT, not code unit: `name[0]` is half a surrogate
                pair, so an emoji-leading group name drew a replacement glyph
                as its initial (OSO-632 follow-up). */}
            {Array.from(group.name)[0] ?? ""}
          </span>
        </div>
      )}
      <div className="p-3">
        <div className="flex items-center gap-1.5 mb-0.5">
          <h3 className="font-semibold text-foreground text-sm truncate">
            {group.name}
          </h3>
          {isMember && (
            <span className="shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-primary-muted text-primary-muted-foreground">
              Medlem
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 text-xs text-subtle-foreground mt-0.5">
          <span>{memberCountLabel(group.memberCount)}</span>
          <span className="text-subtle-foreground">&middot;</span>
          <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${privacy.className}`}>
            {privacy.label}
          </span>
        </div>
        {description && (
          <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
            {description}
          </p>
        )}
      </div>
    </Link>
  );
}
